import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import {
  fetchAllocationList,
  fetchAllocationListFailure,
  fetchAllocationListSuccess,
  getDistinctValuesForAllocationList,
  getDistinctValuesForAllocationListFailure,
  getDistinctValuesForAllocationListSuccess,
} from 'redux/slice/allocation/AllocationList.slice';
import {
  setAllocationListUniqueValues,
  setUniqueValues,
} from 'redux/slice/filter/Filter.slice';
import {
  AllocationListPayload,
  IAllocationListResponse,
} from 'redux/types/Allocation.types';
import { allocationEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* fetchAllocationListSaga({
  payload,
}: PayloadAction<AllocationListPayload>) {
  try {
    const { agencyId, data, page, pageSize, isAllocator } = payload;
    const agencyid = agencyId;

    const endpoint = isAllocator
      ? allocationEndpoints.GET_ALLOCATOR_ALLOCATION_LIST(page, pageSize)
      : allocationEndpoints.GET_ALLOCATION_LIST(agencyid, page, pageSize);

    const response: AxiosResponse<IAllocationListResponse> =
      yield HTTPClient.PostRequest(endpoint, data);

    const allocationData = response.data;
    yield put(fetchAllocationListSuccess(allocationData));
  } catch (error) {
    yield put(
      fetchAllocationListFailure({
        error: { message: 'Failed to fetch allocation list' },
      })
    );
  }
}

// Saga for fetching distinct values for allocation list filters
function* getDistinctValuesForAllocationListSaga(
  action: PayloadAction<{ agencyId: string; columnName: string; filters?: any }>
) {
  try {
    const { agencyId, columnName, filters } = action.payload;
    let uniqueValuesWithMetadata: Array<{ id: string; name: string }> = [];
    let stringValues: string[] = [];

    // Different API endpoints and response formats for different columns
    if (columnName === 'uploadedBy' || columnName === 'uploader') {
      // Uploaded By / Uploader: POST request to ingestion API via gateway
      const endpoint =
        columnName === 'uploadedBy'
          ? allocationEndpoints.GET_ALLOCATION_LIST_DISTINCT_VALUES_FOR_UPLOADED_BY(
              agencyId
            )
          : allocationEndpoints.GET_ALLOCATION_LIST_DISTINCT_VALUES_FOR_UPLOADER(
              agencyId
            );

      const response: AxiosResponse<{
        status: string;
        message: string;
        data: {
          columnName: string;
          distinctValues: Array<{ id: string; name: string }>;
        };
      }> = yield HTTPClient.PostRequest(endpoint, { filters });

      // Transform to standard format
      uniqueValuesWithMetadata = response.data.data.distinctValues.map(
        (item) => ({
          id: item.id,
          name: item.name,
        })
      );
      stringValues = response.data.data.distinctValues.map((item) => item.name);
    } else if (columnName === 'createdOn') {
      // Created On: POST request to ingestion API
      const response: AxiosResponse<{
        status: string;
        message: string;
        data: {
          columnName: string;
          distinctValues: string[];
        };
      }> = yield HTTPClient.PostRequest(
        allocationEndpoints.GET_ALLOCATION_LIST_DISTINCT_VALUES_FOR_CREATED_ON(
          agencyId
        ),
        { filters }
      );

      // Transform to standard format (id and name are same for dates)
      uniqueValuesWithMetadata = response.data.data.distinctValues.map(
        (value: string) => ({
          id: value,
          name: value,
        })
      );
      stringValues = response.data.data.distinctValues;
    } else {
      // Fallback: use generic endpoint
      const response: AxiosResponse<{
        status: string;
        message: string;
        data: {
          columnName: string;
          distinctValues: string[];
        };
      }> = yield HTTPClient.PostRequest(
        allocationEndpoints.GET_ALLOCATION_LIST_DISTINCT_VALUES(
          agencyId,
          columnName
        ),
        { filters }
      );

      uniqueValuesWithMetadata = response.data.data.distinctValues.map(
        (value: string) => ({
          id: value,
          name: value,
        })
      );
      stringValues = response.data.data.distinctValues;
    }

    yield put(
      getDistinctValuesForAllocationListSuccess({
        distinctValues: stringValues,
      })
    );

    yield put(setAllocationListUniqueValues(uniqueValuesWithMetadata));

    // Update the filter slice with string values for FilterSortDropdown backward compatibility
    yield put(setUniqueValues(stringValues));
  } catch (error: any) {
    yield put(
      getDistinctValuesForAllocationListFailure({
        error: { message: error?.message || 'Failed to fetch distinct values' },
      })
    );
    message.error('Failed to fetch filter values');
  }
}

export function* watchAllocationListSaga() {
  yield takeLatest(fetchAllocationList.type, fetchAllocationListSaga);
  yield takeLatest(
    getDistinctValuesForAllocationList.type,
    getDistinctValuesForAllocationListSaga
  );
}
